import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { ProductSpecCategory } from '@/gql/graphql';
import { Button, EmptyState } from '@payaca/ambrosia';
import {
  PinnedTabsContent,
  PinnedTabsTrigger,
  SortableTabs,
  SortableTabsContent,
  SortableTabsHandle,
  SortableTabsItem,
  SortableTabsList,
  SortableTabsTrigger,
} from '@payaca/dnd';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { v4 as uuid } from 'uuid';
import {
  useOptimisticAddEstimateSection,
  useOptimisticRemoveEstimateSection,
  useOptimisticReorderEstimateSections,
} from './api/mutations';
import GrantsTab from './components/GrantsTab';
import OptionsTab from './components/OptionsTab';
import SectionEditor from './components/SectionEditor';
import { EstimateSection } from './types';

const OPTIONS_TAB_VALUE = 'options';
const GRANTS_TAB_VALUE = 'grants';

type DetailPanelProps = {
  projectId: string;
  estimateId: string;
  activeSectionId: string | null;
  onSetActiveSectionId: (id: string | null) => void;
  onAddLineItem: (sectionId: string) => void;
  onEditLineItem: (lineItemId: string) => void;
  onAddAppliance: (category: ProductSpecCategory, sectionId: string) => void;
};

const DetailPanel: FC<DetailPanelProps> = (props) => {
  const {
    projectId,
    estimateId,
    activeSectionId,
    onSetActiveSectionId,
    onAddLineItem,
    onEditLineItem,
    onAddAppliance,
  } = props;

  /**
   * queries
   */
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const sections = estimate?.sections ?? [];

  /**
   * mutations
   */
  const addEstimateSection = useOptimisticAddEstimateSection({
    projectId,
    estimateId,
    options: {
      onMutate: ({ publicId }) => {
        onSetActiveSectionId(publicId);
      },
    },
  });
  const removeEstimateSection = useOptimisticRemoveEstimateSection({
    projectId,
    estimateId,
  });
  const reorderEstimateSections = useOptimisticReorderEstimateSections({
    projectId,
    estimateId,
  });

  /**
   * callbacks
   */
  const handleAdd = () => {
    const publicId = uuid();
    addEstimateSection.mutate({
      estimateId,
      heading: `Section ${sections.length + 1}`,
      publicId,
    });
  };
  const handleSectionDragEnd = (
    _activeItem: EstimateSection,
    _destinationIndex: number,
    items: EstimateSection[]
  ) => {
    reorderEstimateSections.mutate({
      sectionIds: items.map((s) => s.id),
    });
  };
  const handleConfirmDelete = (sectionToDeleteId: string) => {
    if (!sectionToDeleteId) return;
    if (activeSectionId === sectionToDeleteId) {
      const idx = sections.findIndex((s) => s.id === sectionToDeleteId);
      const neighbour = sections[idx + 1] ?? sections[idx - 1];
      onSetActiveSectionId(neighbour?.id ?? null);
    }
    removeEstimateSection.mutate({ id: sectionToDeleteId });
  };

  if (sections.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4">
        <EmptyState
          iconName="link-broken-01.3"
          text="No sections on this estimate"
        />

        <Button size="sm" onClick={handleAdd}>
          Add section
        </Button>
      </div>
    );
  }

  // `activeSectionId` is a section publicId for section tabs or the literal
  // value of a pinned tab. Fall back to the first section when the stored
  // value no longer maps to a known tab.
  const activeTabValue =
    activeSectionId === OPTIONS_TAB_VALUE ||
    activeSectionId === GRANTS_TAB_VALUE
      ? activeSectionId
      : (sections.find((s) => s.id === activeSectionId)?.id ?? sections[0].id);

  return (
    <SortableTabs
      value={activeTabValue}
      onValueChange={onSetActiveSectionId}
      items={sections}
      onReorder={handleSectionDragEnd}
      className="min-h-0 flex-1 h-full flex flex-col"
    >
      {(displaySections) => (
        <>
          <div className="flex items-center gap-x-1 p-3.5">
            <SortableTabsList aria-label="Estimate sections">
              {/* Pinned (non-sortable) leading tabs — must live inside the
                  Tabs.List that SortableTabsList renders so Radix's
                  RovingFocusGroup picks them up alongside the section
                  triggers. */}
              <PinnedTabsTrigger
                value={OPTIONS_TAB_VALUE}
                className="tab tab--pilled"
              >
                Options
              </PinnedTabsTrigger>
              <PinnedTabsTrigger
                value={GRANTS_TAB_VALUE}
                className="tab tab--pilled"
              >
                Grants
              </PinnedTabsTrigger>
              {displaySections.map((section) => (
                <SortableTabsItem key={section.id} value={section.id}>
                  <SortableTabsHandle label={section.heading} />
                  <SortableTabsTrigger value={section.id}>
                    {section.heading}
                  </SortableTabsTrigger>
                </SortableTabsItem>
              ))}
            </SortableTabsList>

            <div className="bg-white shrink-0 ml-auto">
              <Button
                className="whitespace-nowrap"
                size="sm"
                variant="tertiary"
                onClick={handleAdd}
              >
                <UntitledIcon name="plus.3" className="size-4" />
                Add section
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0">
            <PinnedTabsContent
              value={OPTIONS_TAB_VALUE}
              className="h-full overflow-y-auto"
            >
              <OptionsTab projectId={projectId} estimateId={estimateId} />
            </PinnedTabsContent>
            <PinnedTabsContent
              value={GRANTS_TAB_VALUE}
              className="h-full overflow-y-auto"
            >
              <GrantsTab projectId={projectId} estimateId={estimateId} />
            </PinnedTabsContent>
            {displaySections.map((section) => (
              <SortableTabsContent
                key={section.id}
                value={section.id}
                className="h-full"
              >
                <SectionEditor
                  projectId={projectId}
                  estimateId={estimateId}
                  sectionId={section.id}
                  handleConfirmDelete={handleConfirmDelete}
                  onAddLineItem={onAddLineItem}
                  onEditLineItem={onEditLineItem}
                  onAddAppliance={onAddAppliance}
                />
              </SortableTabsContent>
            ))}
          </div>
        </>
      )}
    </SortableTabs>
  );
};

export default DetailPanel;
